import React, { useState, useEffect } from 'react';
import { DataVisualizer } from './DataVisualizer';
import { exportQueryResponse, exportCachedQuery, getExportLabel, isTableVisualization } from '../services/exportService';
import { paginateQuery } from '../services/databaseService';
import './QueryResultCard.css';

export interface PaginationMeta {
    process_id: string;
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
}

export interface QueryCardData {
    process_id: string;
    batch_id?: string;
    question: string;
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'ERROR' | 'CANCELLED';
    statusMessage?: string;
    result?: {
        query: string;
        data: any[];
        visualization: string;
        summary: string;
        result_count: number;
    };
    error?: string;
    completed_at?: Date;
    result_count?: number;
    pagination?: PaginationMeta;
}

interface QueryResultCardProps {
    query: QueryCardData;
    connectionName?: string | null;
    pageSessionKey?: string;
    onAskMore?: (query: QueryCardData) => void;
    onSaveWidget?: (query: QueryCardData) => void;
    onRetry?: (query: QueryCardData) => void;
    onClear?: (process_id: string) => void;
    onExpand?: (query: QueryCardData) => void;
    onCancel?: (query: QueryCardData) => void;
}

const STATUS_CONFIG = {
    PENDING: { label: 'PENDING', badgeClass: 'qrc-badge-pending' },
    PROCESSING: { label: 'PROCESSING', badgeClass: 'qrc-badge-processing' },
    COMPLETED: { label: 'COMPLETED', badgeClass: 'qrc-badge-completed' },
    ERROR: { label: 'ERROR', badgeClass: 'qrc-badge-error' },
    CANCELLED: { label: 'CANCELLED', badgeClass: 'qrc-badge-cancelled' },
};

/** Returns true if visualization is a metric/single-value card (no export, no expand) */
const isMetricVisualization = (viz: string | undefined): boolean => {
    if (!viz) return false;
    return ['metric', 'metric_card', 'single_value', 'kpi', 'number'].includes(viz.toLowerCase());
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const QueryResultCard: React.FC<QueryResultCardProps> = ({
    query,
    connectionName,
    pageSessionKey,
    onAskMore,
    onSaveWidget,
    onRetry,
    onClear,
    onExpand,
    onCancel,
}) => {
    const [isExporting, setIsExporting] = useState(false);

    // Pagination state — initialized from first-page result
    const initPagination = query.pagination;
    const [currentPage, setCurrentPage] = useState(initPagination?.page ?? 1);
    const [pageSize, setPageSize] = useState(initPagination?.page_size ?? 10);
    const [totalCount, setTotalCount] = useState(initPagination?.total_count ?? 0);
    const [totalPages, setTotalPages] = useState(initPagination?.total_pages ?? 1);
    const [pageData, setPageData] = useState<any[] | null>(null); // null = use result.data
    const [isLoadingPage, setIsLoadingPage] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);

    // Reset pagination state when query result changes (new question completed)
    useEffect(() => {
        if (query.pagination) {
            setCurrentPage(query.pagination.page);
            setPageSize(query.pagination.page_size);
            setTotalCount(query.pagination.total_count);
            setTotalPages(query.pagination.total_pages);
        }
        setPageData(null);
        setPageError(null);
    }, [query.process_id, query.status]);

    const handlePageChange = async (newPage: number, newPageSize?: number) => {
        const effectivePageSize = newPageSize ?? pageSize;
        const processId = query.pagination?.process_id ?? query.process_id;
        if (!pageSessionKey || !processId) return;

        setIsLoadingPage(true);
        setPageError(null);
        try {
            const res = await paginateQuery({
                page_session_key: pageSessionKey,
                process_id: processId,
                page: newPage,
                page_size: effectivePageSize,
            });
            setPageData(res.data);
            setCurrentPage(res.page);
            setPageSize(res.page_size);
            setTotalCount(res.total_count);
            setTotalPages(res.total_pages);
        } catch (err: any) {
            setPageError(err?.message || 'Failed to load page');
        } finally {
            setIsLoadingPage(false);
        }
    };

    const status = query.status || 'PENDING';
    const isLoading = status === 'PENDING' || status === 'PROCESSING';
    const isCompleted = status === 'COMPLETED';
    const isError = status === 'ERROR';
    const isCancelled = status === 'CANCELLED';
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
    const isMetric = isCompleted && query.result ? isMetricVisualization(query.result.visualization) : false;

    const handleExport = async () => {
        if (!query.result || isExporting) return;
        setIsExporting(true);
        try {
            const processId = query.pagination?.process_id ?? query.process_id;
            const hasMoreThanOnePage = (totalCount || 0) > (query.result.data?.length ?? 0);
            // Export the FULL result set (all matching rows) when the answer
            // spans more than the visible page; otherwise export what's shown.
            if (pageSessionKey && processId && hasMoreThanOnePage) {
                await exportCachedQuery(
                    pageSessionKey,
                    processId,
                    query.question,
                    query.result.visualization,
                    query.result.query,
                );
            } else {
                await exportQueryResponse(
                    query.result.data,
                    query.question,
                    query.result.visualization,
                    query.result.query
                );
            }
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className={`query-result-card ${isError ? 'qrc-error-border' : ''}`}>
            {/* ── Header ── */}
            <div className="qrc-header">
                <div className={`qrc-header-icon ${status.toLowerCase()}`}>
                    {isLoading && <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>analytics</span>}
                    {isCompleted && <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>check_circle</span>}
                    {isError && <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>error</span>}
                </div>
                <h3 className="qrc-title">{query.question}</h3>
                {isCompleted && !isMetric && onExpand && (
                    <button className="qrc-btn-expand-icon" onClick={() => onExpand(query)} title="Expand to full screen">
                        <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>open_in_full</span>
                    </button>
                )}
                <span className={`qrc-badge ${cfg.badgeClass}`}>{cfg.label}</span>
            </div>

            {/* ── Loading / Processing — Skeleton + Orbit ── */}
            {isLoading && (
                <div className="qrc-loading">
                    <div className="qrc-skeleton-group">
                        <div className="qrc-skeleton-bar qrc-sk-w80" />
                        <div className="qrc-skeleton-bar qrc-sk-w55" />
                    </div>

                    <div className="qrc-orbit-container">
                        <div className="qrc-orbit-ring">
                            <div className="qrc-orbit-dot" />
                        </div>
                        <div className="qrc-orbit-icon">
                            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>query_stats</span>
                        </div>
                    </div>

                    <div className="qrc-skeleton-group">
                        <div className="qrc-skeleton-bar qrc-sk-w70" />
                        <div className="qrc-skeleton-bar qrc-sk-w45" />
                        <div className="qrc-skeleton-bar qrc-sk-w60" />
                    </div>

                    <div className="qrc-loading-status">
                        <div className="qrc-status-dot" />
                        <span>{query.statusMessage || 'Analyzing data...'}</span>
                    </div>
                    {connectionName && (
                        <div className="qrc-loading-conn">
                            <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>database</span>
                            {connectionName}
                        </div>
                    )}
                    <div className="qrc-progress-bar">
                        <div className="qrc-progress-fill" />
                    </div>
                    {onCancel && (
                        <button className="qrc-btn qrc-btn-cancel" onClick={() => onCancel(query)}>
                            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
                            Cancel
                        </button>
                    )}
                </div>
            )}

            {/* ── Completed ── */}
            {isCompleted && query.result && (
                <>
                    <div className="qrc-content">
                        {query.result.summary && (
                            <div className="qrc-summary">
                                <span className="material-symbols-outlined qrc-summary-icon" style={{ fontSize: '18px' }}>lightbulb</span>
                                <p>{query.result.summary}</p>
                            </div>
                        )}
                        {/* Prefer pagination total_count; fall back to result_count for non-paginated results */}
                        {(totalCount > 0 || (query.result.result_count || 0) > 0) && (
                            <div className="qrc-result-count">
                                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>table_rows</span>
                                <span>{totalCount || query.result.result_count} rows returned</span>
                            </div>
                        )}
                        <div className="qrc-visualization">
                            <DataVisualizer
                                data={pageData ?? query.result.data}
                                visualizationType={query.result.visualization}
                            />
                        </div>
                        {pageError && (
                            <div className="qrc-page-error">
                                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>warning</span>
                                {pageError}
                            </div>
                        )}
                    </div>

                    {/* ── Pagination controls ── */}
                    {isTableVisualization(query.result.visualization) && totalPages > 1 && pageSessionKey && query.pagination && (
                        <div className="qrc-pagination">
                            <div className="qrc-pagination-info">
                                {(() => {
                                    const start = (currentPage - 1) * pageSize + 1;
                                    const end = Math.min(currentPage * pageSize, totalCount);
                                    return <span>Showing {start}–{end} of {totalCount}</span>;
                                })()}
                            </div>
                            <div className="qrc-pagination-controls">
                                <button
                                    className="qrc-page-btn"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage <= 1 || isLoadingPage}
                                    title="Previous page"
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_left</span>
                                </button>
                                <span className="qrc-page-indicator">
                                    {isLoadingPage
                                        ? <span className="qrc-page-spinner" />
                                        : `${currentPage} / ${totalPages}`
                                    }
                                </span>
                                <button
                                    className="qrc-page-btn"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage >= totalPages || isLoadingPage}
                                    title="Next page"
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chevron_right</span>
                                </button>
                            </div>
                            <div className="qrc-pagination-size">
                                <select
                                    className="qrc-page-size-select"
                                    value={pageSize}
                                    onChange={(e) => {
                                        const newSize = parseInt(e.target.value, 10);
                                        handlePageChange(1, newSize);
                                    }}
                                    disabled={isLoadingPage}
                                >
                                    {PAGE_SIZE_OPTIONS.map((n) => (
                                        <option key={n} value={n}>{n} / page</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="qrc-footer">
                        {!isMetric && (
                            <button
                                className="qrc-btn qrc-btn-export"
                                onClick={handleExport}
                                disabled={isExporting}
                                title={getExportLabel(query.result.visualization)}
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                                    {isExporting ? 'hourglass_empty' : (isTableVisualization(query.result.visualization) ? 'table_view' : 'picture_as_pdf')}
                                </span>
                                {isExporting ? 'Exporting...' : getExportLabel(query.result.visualization)}
                            </button>
                        )}
                        {onAskMore && (
                            <button className="qrc-btn qrc-btn-askmore" onClick={() => onAskMore(query)}>
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chat</span>
                                Ask More
                            </button>
                        )}
                        {onSaveWidget && (
                            <button className="qrc-btn qrc-btn-save" onClick={() => onSaveWidget(query)}>
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>save</span>
                                Save Widget
                            </button>
                        )}
                        {onClear && (
                            <button className="qrc-btn qrc-btn-clear" onClick={() => onClear(query.process_id)}>
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>delete</span>
                                Clear
                            </button>
                        )}
                    </div>
                </>
            )}

            {/* ── Error ── */}
            {isError && (
                <>
                    <div className="qrc-error">
                        <div className="qrc-error-icon">
                            <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>error</span>
                        </div>
                        <h4>Query Failed</h4>
                        <p>{query.error || 'An unexpected error occurred'}</p>
                    </div>
                    <div className="qrc-footer">
                        {onRetry && (
                            <button className="qrc-btn qrc-btn-retry" onClick={() => onRetry(query)}>
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>refresh</span>
                                Retry
                            </button>
                        )}
                        {onClear && (
                            <button className="qrc-btn qrc-btn-clear" onClick={() => onClear(query.process_id)}>
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>delete</span>
                                Clear
                            </button>
                        )}
                    </div>
                </>
            )}

            {/* ── Cancelled ── */}
            {isCancelled && (
                <>
                    <div className="qrc-error">
                        <div className="qrc-error-icon">
                            <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>cancel</span>
                        </div>
                        <h4>Query Cancelled</h4>
                        <p>{query.error || 'This query was cancelled by user.'}</p>
                    </div>
                    <div className="qrc-footer">
                        {onRetry && (
                            <button className="qrc-btn qrc-btn-retry" onClick={() => onRetry(query)}>
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>refresh</span>
                                Retry
                            </button>
                        )}
                        {onClear && (
                            <button className="qrc-btn qrc-btn-clear" onClick={() => onClear(query.process_id)}>
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>delete</span>
                                Clear
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};
